import { FC, ReactElement, ReactNode, RefObject, useCallback, useEffect, useState } from 'react';

import TooltipUI from './TooltipUI';
import { TextProps } from '../Text';
import { Coords, getCoords } from '../utils/lib/dom';

// Local Typings
type TooltipPlacement = 'bottom' | 'left' | 'right' | 'top';
export interface TooltipProps {
  alignment?: TextProps['alignment'];
  childRef: RefObject<HTMLElement | null>;
  children: ReactElement;
  noWrap?: boolean;
  tooltipLabel: ReactNode;
  tooltipPlacement: TooltipPlacement;
}

// Component Definition
const Tooltip: FC<TooltipProps> = props => {
  const [isOpen, setIsOpen] = useState(false);
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (el) {
      setCoords(getCoords(el));
    }
  },
  [el]);

  function handleMouseLeave() {
    setCoords(null);
  }

  useEffect(() => {
    setEl(props.childRef.current);
  },
  [props.childRef]);

  useEffect(() => {
    if (el) {
      el.addEventListener('mouseenter',
        handleMouseEnter);
      el.addEventListener('mouseleave',
        handleMouseLeave);
      el.addEventListener('click',
        handleMouseLeave);

      return () => {
        el.removeEventListener('mouseenter',
          handleMouseEnter);
        el.removeEventListener('mouseleave',
          handleMouseLeave);
      };
    }
  },
  [el, handleMouseEnter]);

  useEffect(() => {
    window.addEventListener('scroll',
      handleMouseLeave);

    return () => {
      window.removeEventListener('scroll',
        handleMouseLeave);
    };
  },
  []);

  useEffect(() => {
    if (coords) {
      const timeout = setTimeout(() => {
        setIsOpen(true);
      },
      300);

      return () => {
        clearTimeout(timeout);
      };
    }
    setIsOpen(false);
  },
  [coords]);

  return (
    <>
      {props.children}

      {coords && isOpen && (
        <TooltipUI
          alignment={props.alignment}
          coords={coords}
          noWrap={props.noWrap}
          tooltipLabel={props.tooltipLabel}
          tooltipPlacement={props.tooltipPlacement}
        />
      )}
    </>
  );
};

export default Tooltip;
